import React, { useEffect, useState } from "react";
import { CheckCircle2, Download, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, BACKEND_URL } from "../lib/api";
import { SignatureCanvas } from "../pages/AgreementSignPage";

export function useWaiverTerms() {
  const [t, setT] = useState(null);
  useEffect(() => { api.get("/waiver/terms").then((r) => setT(r.data)).catch(() => {}); }, []);
  return t;
}

export const waiverPdfUrl = (w) => `${BACKEND_URL}/api/waiver/${w.waiver_id}/pdf?token=${w.token}`;

// The simple 1-2-3 Photo & Media Release, shared by users (typed name) and guests (drawn signature).
export function WaiverForm({ submit, drawn = false, withEmail = false, onSigned, cta = "Sign & continue" }) {
  const terms = useWaiverTerms();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [sig, setSig] = useState(null);
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  async function go() {
    setBusy(true);
    try {
      const w = { full_name: name.trim(), ...(await submit({ full_name: name, agree, signature_data_url: sig, ...(withEmail && email ? { email } : {}) })) };
      setDone(w);
      toast.success("Signed. Thank you!");
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  }
  if (done) {
    return (
      <div className="text-center space-y-4" data-testid="waiver-done">
        <CheckCircle2 className="w-12 h-12 text-[var(--gold)] mx-auto" />
        <p className="font-serif text-3xl">All set!</p>
        <a href={waiverPdfUrl(done)} className="btn-outline-gold inline-flex" data-testid="waiver-pdf"><Download className="w-4 h-4" /> Save my copy (PDF)</a>
        <div><button onClick={() => onSigned(done)} className="btn-gold" data-testid="waiver-continue">Continue</button></div>
      </div>
    );
  }
  const step = (n, title, body) => (
    <div className="flex gap-4"><span className="w-8 h-8 shrink-0 rounded-full bg-[var(--ink)] text-white flex items-center justify-center font-serif">{n}</span><div className="flex-1 space-y-2"><p className="font-medium">{title}</p>{body}</div></div>
  );
  return (
    <div className="space-y-6" data-testid="waiver-form">
      <p className="text-[11px] uppercase tracking-[0.25em] text-[var(--gold)] flex items-center gap-1.5"><ShieldCheck className="w-4 h-4" /> {terms?.title || "Photo & Media Release"} · 30 seconds</p>
      {step(1, "Read", <ul className="text-sm text-[var(--muted)] space-y-1.5 list-disc pl-4" data-testid="waiver-points">{terms?.points.map((p) => <li key={p}>{p}</li>)}</ul>)}
      {step(2, drawn ? "Your name & signature" : "Type your full name", <>
        <input value={name} onChange={(e) => setName(e.target.value)} maxLength={120} placeholder="Full name" className="input-luxe" data-testid="waiver-name" />
        {withEmail && <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email (optional, for your copy)" className="input-luxe" data-testid="waiver-email" />}
        {drawn && <SignatureCanvas onChange={setSig} />}
      </>)}
      {step(3, "Agree", <label className="flex items-start gap-2 text-sm"><input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} className="mt-1" data-testid="waiver-agree" /> I agree to the {terms?.title || "Photo & Media Release"} and to signing electronically.</label>)}
      <button onClick={go} disabled={busy || !agree || name.trim().length < 2 || (drawn && !sig)} className="btn-gold w-full disabled:opacity-50 disabled:cursor-not-allowed" data-testid="waiver-submit">{busy ? "Signing…" : cta}</button>
    </div>
  );
}
